/**
 * /operations/loans/arrears — Session 3a PR 13.
 *
 * Loans with 3+ missed installments · HR action required · arrears
 * escalation workflow. Mutaba'aa-owned per Session 3a §4.12 (Article
 * 80 absconding implications · escalation thresholds).
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Mutaba'aa narration)
 *   2. InlineAISuggestion cards (Article 80 critical · departmental
 *      pattern)
 *   3. TraceableValue wrapping outstanding + consecutive_missed +
 *      total_missed_amount (≥3 wrappers)
 */

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Scan, ShieldX } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../components/ui";
import {
  session3aApi,
  type ArrearsItem,
} from "../lib/session3a-api";
import { useLegalEntityGate } from "../lib/legalEntity";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { formatSar, severityChip, showAlertTrace } from "./_session3aHelpers";

const ABSCONDING_THRESHOLD = 6;

function missedTone(n: number): "neutral" | "attention" | "critical" {
  if (n >= ABSCONDING_THRESHOLD) return "critical";
  if (n >= 3) return "attention";
  return "neutral";
}

export function LoanArrearsPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [filter, setFilter] = useState<
    "all" | "three_to_five" | "six_plus" | "not_yet_escalated" | "in_dispute"
  >("not_yet_escalated");

  const list = useQuery({
    queryKey: ["loan-arrears", legalEntityId],
    queryFn: () => session3aApi.listArrears(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: ArrearsItem[] = list.data?.items ?? [];

  const stats = useMemo(() => {
    const critical = items.filter((r) => r.consecutive_missed >= ABSCONDING_THRESHOLD);
    const longArrears = items.filter((r) => r.months_since_last_payment >= 4);
    const notYetEscalated = items.filter((r) => r.status === "normal");
    const sumOut = items.reduce((acc, r) => acc + r.outstanding_sar, 0);
    return {
      total: items.length,
      critical: critical.length,
      criticalEmployee: critical[0],
      longArrears: longArrears.length,
      notYetEscalated: notYetEscalated.length,
      sumOut,
    };
  }, [items]);

  const filtered = useMemo(() => {
    switch (filter) {
      case "three_to_five":
        return items.filter(
          (r) => r.consecutive_missed >= 3 && r.consecutive_missed < ABSCONDING_THRESHOLD,
        );
      case "six_plus":
        return items.filter((r) => r.consecutive_missed >= ABSCONDING_THRESHOLD);
      case "not_yet_escalated":
        return items.filter((r) => r.status === "normal");
      case "in_dispute":
        return items.filter((r) => r.status === "in_dispute");
      default:
        return items;
    }
  }, [items, filter]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("operations.loans_arrears.title", "Loan Arrears")}
          subtitle={t(
            "operations.loans_arrears.subtitle",
            "Loans with 3+ consecutive missed installments. Mutaba'aa flags Article 80 absconding pre-conditions; HR escalates from here.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(
              [
                ["all", "All"],
                ["three_to_five", "3-5 missed"],
                ["six_plus", "6+ missed"],
                ["not_yet_escalated", "Not yet escalated"],
                ["in_dispute", "In dispute"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  filter === key
                    ? key === "six_plus"
                      ? "border-rose-300 bg-rose-50 text-rose-800"
                      : key === "three_to_five"
                        ? "border-amber-300 bg-amber-50 text-amber-800"
                        : "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`operations.loans_arrears.filter.${key}`, label)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "operations.loans_arrears.audit_note",
                  "Manual trigger runs Mutaba'aa's arrears scanner immediately. APScheduler also runs the scan daily.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Scan className="h-4 w-4" />
            {t("operations.loans_arrears.cta.audit", "Run arrears audit")}
          </button>
        </div>

        {stats.criticalEmployee && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<AlertTriangle className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "operations.loans_arrears.suggestion.critical_headline",
              `Mutaba'aa critical: ${stats.criticalEmployee.employee_name_en} — ${stats.criticalEmployee.consecutive_missed} missed payments · Article 80 absconding pre-conditions may apply.`,
            )}
            body={t(
              "operations.loans_arrears.suggestion.critical_body",
              "Escalate to HR Director now. Article 80 requires the full 5-condition checklist before any absconded EOS path triggers.",
            )}
            primaryAction={{
              label: t(
                "operations.loans_arrears.suggestion.escalate",
                "Escalate to HR",
              ),
              onClick: () =>
                showAlertTrace(
                  t(
                    "operations.loans_arrears.suggestion.escalate_note",
                    "Escalation writes a workflow_event with kind='arrears_escalation' and routes to HR Director.",
                  ),
                ),
            }}
            suggestionKey={`arrears-critical-${stats.criticalEmployee.application_id}`}
          />
        )}
        {stats.total > 3 && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<ShieldX className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "operations.loans_arrears.suggestion.pattern_headline",
              `Tashghil pattern: ${stats.total} arrears across the LE — departmental compensation health worth reviewing.`,
            )}
            body={t(
              "operations.loans_arrears.suggestion.pattern_body",
              "Cross-reference with comp bands; arrears often cluster where pay lags peer roles.",
            )}
            primaryAction={{
              label: t(
                "operations.loans_arrears.suggestion.open_comp",
                "Open Compensation report",
              ),
              onClick: () => window.location.assign("/reports/compensation"),
            }}
            suggestionKey={`arrears-pattern-${stats.total}`}
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("operations.loans_arrears.col.application", "Application")}</th>
                  <th className="px-3 py-2">{t("operations.loans_arrears.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("operations.loans_arrears.col.outstanding", "Outstanding")}</th>
                  <th className="px-3 py-2">{t("operations.loans_arrears.col.missed", "Missed")}</th>
                  <th className="px-3 py-2">{t("operations.loans_arrears.col.last_payment", "Last payment")}</th>
                  <th className="px-3 py-2">{t("operations.loans_arrears.col.missed_amount", "Total missed (SAR)")}</th>
                  <th className="px-3 py-2">{t("operations.loans_arrears.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("operations.loans_arrears.col.escalation_date", "Escalation")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.application_id}
                    className="border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/operations/loans/applications/${row.application_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.application_id.slice(0, 8)}…
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="text-text-secondary hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.loans_arrears.trace.outstanding",
                              `outstanding = principal − installments_paid + interest_accrued. Includes the missed installment principal.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr" className="font-medium text-text-primary">
                          {formatSar(row.outstanding_sar)}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.loans_arrears.trace.missed",
                              `consecutive_missed = ${row.consecutive_missed}. 6+ triggers Article 80 absconding flag (requires HR-completed 5-condition checklist before EOS path applies).`,
                            ),
                          )
                        }
                      >
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(missedTone(row.consecutive_missed))}`}
                        >
                          {row.consecutive_missed}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.last_successful_payment_date ?? "—"}</span>
                      <div className="text-text-tertiary">
                        {row.months_since_last_payment}{" "}
                        {t("operations.loans_arrears.months_ago", "months ago")}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.loans_arrears.trace.missed_amount",
                              `total_missed_amount = sum of skipped installments. Recovered via M30 EOS workflow or amended schedule.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr">{formatSar(row.total_missed_amount_sar)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(
                          row.status === "written_off"
                            ? "neutral"
                            : row.status === "in_dispute"
                              ? "attention"
                              : row.status === "arrears_escalated"
                                ? "critical"
                                : "neutral",
                        )}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      {row.escalation_date ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "operations.loans_arrears.empty",
              "No loans in arrears · all installments tracking on schedule. ✦ Mutaba'aa: healthy.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="mutabaa"
          liveContext={{
            total: stats.total,
            attention: stats.notYetEscalated,
            critical: stats.critical,
            top_kind: t("operations.loans_arrears.title", "Loan Arrears"),
            window_label: filter,
          }}
        />
      </aside>
    </div>
  );
}
